import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TextEditor = (
  { label, placeholder, onChange, value, error, imageUploadFn },
  forwardedRef,
) => {
  const text = error || label || '';

  const quillRef = useRef(null);

  const setRefs = useCallback(
    (instance) => {
      quillRef.current = instance;
      if (!forwardedRef) return;
      if (typeof forwardedRef === 'function') forwardedRef(instance);
      else forwardedRef.current = instance;
    },
    [forwardedRef],
  );

  const handleImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      try {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;
        const range = editor.getSelection(true);

        let imageSrc = null;

        if (typeof imageUploadFn === 'function') {
          imageSrc = await imageUploadFn(file);
        } else {
          imageSrc = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read image'));
            reader.readAsDataURL(file);
          });
        }

        if (!imageSrc) return;
        editor.insertEmbed(range?.index ?? 0, 'image', imageSrc, 'user');
        editor.setSelection((range?.index ?? 0) + 1, 0);
      } catch (e) {
        console.error('TextEditor image insert failed:', e);
      }
    };
  }, [imageUploadFn]);

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Header sizes
          [{ font: [] }], // Font selection
          ['bold', 'italic', 'underline', 'strike'], // Formatting options
          [{ color: [] }, { background: [] }], // Text color and background color
          [{ list: 'ordered' }, { list: 'bullet' }], // Lists
          [{ align: [] }], // Text alignment
          ['link', 'image'], // Links and images
          ['clean'], // Clear formatting
        ],
        handlers: {
          image: handleImage,
        },
      },
    };
  }, [handleImage]);

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'align',
    'link',
    'image',
  ];

  return (
    <div className="w--full d--flex flex--column">
      {text && (
        <label
          className={`label--control font--sm font--500 m-b--sm ${
            error ? 'text--danger' : 'text--black'
          }`}
        >
          {text}
        </label>
      )}
      <div className="w--full">
        <ReactQuill
          ref={setRefs}
          value={value}
          modules={modules}
          formats={formats}
          onChange={(content) => onChange(content)}
          placeholder={placeholder || 'Start typing...'}
          className={`${error ? 'border--danger' : ''}`}
        />
      </div>
    </div>
  );
};

export default forwardRef(TextEditor);
